import React, { useState, useEffect, useCallback } from 'react';
import { Download, X, Share2 } from 'lucide-react';

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  // Check if already running as installed PWA
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone
  );

  useEffect(() => {
    // Never show if running in standalone mode (already using installed app)
    if (isStandalone) return;

    // Check "don't show for a week" dismiss
    const longDismissed = localStorage.getItem('pwa-install-dismissed');
    if (longDismissed && Date.now() - parseInt(longDismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // Check session dismiss (this tab session only)
    const sessionDismissed = sessionStorage.getItem('pwa-install-session-dismissed');
    if (sessionDismissed) return;

    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Capture beforeinstallprompt for native install on Android/Desktop Chrome.
    // IMPORTANT: If this event fires, the browser confirms the PWA is NOT installed.
    // Clear any stale "installed" flag from a previous installation the user deleted.
    const handler = (e) => {
      e.preventDefault();
      localStorage.removeItem('pwa-app-installed');
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Listen for successful app installation
    const installedHandler = () => {
      localStorage.setItem('pwa-app-installed', 'true');
      setShowBanner(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', installedHandler);

    // Show banner after 3s delay
    const showTimer = setTimeout(() => setShowBanner(true), 3000);

    return () => {
      clearTimeout(showTimer);
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, [isStandalone]);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        localStorage.setItem('pwa-app-installed', 'true');
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  const handleDismissSession = () => {
    setShowBanner(false);
    sessionStorage.setItem('pwa-install-session-dismissed', '1');
  };

  const handleDismissLong = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (!showBanner || isStandalone) return null;

  // iOS tip
  if (isIOS) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[60] p-3" style={{ animation: 'slideUp .35s ease-out' }} data-testid="pwa-ios-tip">
        <div className="max-w-lg mx-auto bg-gray-900 text-white rounded-2xl shadow-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Download className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold mb-1">Install GOPIKO App</p>
              <p className="text-xs text-gray-400 leading-relaxed">
                Tap <Share2 className="w-3 h-3 inline-block mx-0.5" /> <strong>Share</strong>, then scroll and tap <strong>"Add to Home Screen"</strong>
              </p>
            </div>
            <button onClick={handleDismissSession} className="text-gray-500 hover:text-white p-1 flex-shrink-0" data-testid="pwa-dismiss-ios">
              <X className="w-4 h-4" />
            </button>
          </div>
          <button onClick={handleDismissLong} className="text-[10px] text-gray-500 hover:text-gray-300 mt-2 ml-13" data-testid="pwa-dismiss-long-ios">
            Don't show for a week
          </button>
        </div>
      </div>
    );
  }

  // Android / Desktop banner
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] p-3 sm:p-4" style={{ animation: 'slideUp .35s ease-out' }} data-testid="pwa-install-banner">
      <div className="max-w-lg mx-auto bg-gray-900 text-white rounded-2xl shadow-2xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold">Install GOPIKO</p>
            <p className="text-xs text-gray-400">Faster access from your home screen</p>
          </div>
          {deferredPrompt ? (
            <button
              onClick={handleInstall}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-bold px-4 py-2 rounded-xl flex-shrink-0 transition-colors"
              data-testid="pwa-install-btn"
            >
              Install
            </button>
          ) : (
            <a
              href="/manifest.json"
              onClick={(e) => { e.preventDefault(); handleDismissSession(); }}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-bold px-4 py-2 rounded-xl flex-shrink-0 transition-colors text-center"
              data-testid="pwa-install-btn"
            >
              Got it
            </a>
          )}
          <button onClick={handleDismissSession} className="text-gray-500 hover:text-white p-1 flex-shrink-0" data-testid="pwa-dismiss-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <button onClick={handleDismissLong} className="text-[10px] text-gray-500 hover:text-gray-300 mt-2 block" data-testid="pwa-dismiss-long">
          Don't show for a week
        </button>
      </div>
    </div>
  );
}
